import { useEffect, useState } from 'react'
import { useBrandAssets } from '@/hooks/cinematics/useBrandAssets'

import settings from '@/settings/public'

/**
 * Hook to load Lottie animation files dynamically
 * @param customPath - Optional custom path to override settings path
 * @returns The loaded Lottie animation data or null
 */
export function useLottieLoader(customPath?: string) {
  const [animationData, setAnimationData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const { lottiePath: brandedLottiePath } = useBrandAssets()

  useEffect(() => {
    const loadLottie = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Use custom path or fall back to settings
        const lottiePath = customPath || brandedLottiePath || settings.images?.lottie

        if (!lottiePath) {
          throw new Error('No Lottie path provided in settings or props')
        }

        // Extract the relative path from the public directory
        // e.g., '/assets/lotties/clients/fanatiz/main/loading.json' -> 'assets/lotties/clients/fanatiz/main/loading.json'
        const relativePath = lottiePath.startsWith('/')
          ? lottiePath.slice(1)
          : lottiePath

        // Dynamic import from public directory
        const response = await fetch(`/${relativePath}`)

        if (!response.ok) {
          throw new Error(`Failed to load Lottie file: ${response.statusText}`)
        }

        const data = await response.json()
        setAnimationData(data)
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error loading Lottie')
        console.error('Failed to load Lottie animation:', error)
        setError(error)
        setAnimationData(null)
      } finally {
        setIsLoading(false)
      }
    }

    loadLottie()
  }, [customPath, brandedLottiePath])

  return { animationData, isLoading, error }
}
